import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MessageSquare, X, ChevronLeft, Send, Plus, Clock, CheckCircle2, Loader2, Check, CheckCheck, Pencil, Search, Users } from 'lucide-react';
import { taskChatApi, ChatPerson, ChatTask, ChatMessage, PresenceMap } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../Toast/Toast';

type View = 'people' | 'board' | 'tasks' | 'thread' | 'new';

const initials = (name: string) => (name || '?').split(/\s+/).slice(0, 2).map(s => s[0]).join('').toUpperCase();
const fmtTime = (s?: string | null) => {
  if (!s) return '';
  const d = new Date(s); if (isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay ? d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : d.toLocaleDateString('en-GB');
};
const statusLabel: Record<string, string> = { pending: 'Pending', in_progress: 'In Progress', awaiting_approval: 'Approval Pending', done: 'Done', cancelled: 'Cancelled' };
const statusDot: Record<string, string> = { pending: 'bg-amber-500', in_progress: 'bg-blue-500', awaiting_approval: 'bg-purple-500', done: 'bg-emerald-500', cancelled: 'bg-gray-300' };
const isOpenStatus = (s: string) => s === 'pending' || s === 'in_progress' || s === 'awaiting_approval';
const priorityStyle: Record<string, string> = { high: 'bg-red-100 text-red-700', normal: 'bg-gray-100 text-gray-500', low: 'bg-sky-100 text-sky-700' };
const fmtDate = (s?: string | null) => {
  if (!s) return '';
  const d = new Date(s); if (isNaN(d.getTime())) return String(s).slice(0, 10);
  return d.toLocaleDateString('en-GB'); // dd/mm/yyyy
};
const overdueDays = (t: ChatTask) => {
  if (!t.due_date || t.status === 'done' || t.status === 'cancelled') return 0;
  const due = new Date(t.due_date); if (isNaN(due.getTime())) return 0;
  due.setHours(23, 59, 59, 999);
  const diff = Math.floor((Date.now() - due.getTime()) / 86400000);
  return diff > 0 ? diff : 0;
};

export default function TaskChatWidget() {
  const { user } = useAuth();
  const { showError, showSuccess } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  // In the native Android app, background notifications come from the native
  // NotificationPollService — so we skip the browser Notification popup there
  // to avoid showing a duplicate.
  const isNativeApp = !!(window as any).Capacitor?.isNativePlatform?.();

  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>('people');
  const [unread, setUnread] = useState(0);
  const [presence, setPresence] = useState<PresenceMap>({});

  const [people, setPeople] = useState<ChatPerson[]>([]);
  const [activeUser, setActiveUser] = useState<{ id: string; name: string } | null>(null);
  const [tasks, setTasks] = useState<ChatTask[]>([]);
  const [taskFilter, setTaskFilter] = useState<'pending' | 'completed' | 'all'>('pending');
  const [taskSort, setTaskSort] = useState<'recent' | 'priority' | 'due'>('recent');
  // Global task board (Phase 3)
  const [boardTasks, setBoardTasks] = useState<ChatTask[]>([]);
  const [boardDir, setBoardDir] = useState<'all' | 'to_me' | 'by_me'>('all');
  const [boardSearch, setBoardSearch] = useState('');
  const [boardDebounced, setBoardDebounced] = useState('');
  const [boardOverdue, setBoardOverdue] = useState(false);
  // Swipe-to-reveal quick actions on a task row (which row is open + touch start x).
  const [swipedId, setSwipedId] = useState<number | null>(null);
  const swipeStartX = useRef<number | null>(null);
  // Draggable floating launcher position (null = default bottom-right); persisted.
  const [launcherPos, setLauncherPos] = useState<{ x: number; y: number } | null>(() => {
    try { const s = localStorage.getItem('taskchat_launcher_pos'); return s ? JSON.parse(s) : null; } catch { return null; }
  });
  const dragRef = useRef({ dragging: false, moved: false, startX: 0, startY: 0, offX: 0, offY: 0 });
  // App-icon / tab badge + desktop-notification bookkeeping.
  const baseTitleRef = useRef<string>(typeof document !== 'undefined' ? document.title : 'ABS Cloud');
  const prevUnreadRef = useRef(0);
  const firstUnreadRef = useRef(true);
  const [activeTask, setActiveTask] = useState<ChatTask | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  // New-task form
  const [users, setUsers] = useState<{ id: string; name: string }[]>([]);
  // assigned_to is a LIST — one id is a normal 1-to-1 task, several make it a
  // group task with a single shared thread.
  const [nf, setNf] = useState<{ title: string; assigned_to: string[]; priority: string; due_date: string; message: string }>(
    { title: '', assigned_to: [], priority: 'normal', due_date: '', message: '' });
  const toggleAssignee = (id: string) => setNf(s => ({
    ...s,
    assigned_to: s.assigned_to.includes(id) ? s.assigned_to.filter(x => x !== id) : [...s.assigned_to, id],
  }));
  // Type-to-filter state for the "Assign to" field.
  const [asgQuery, setAsgQuery] = useState('');
  const [asgOpen, setAsgOpen] = useState(false);
  const asgInputRef = useRef<HTMLInputElement>(null);
  // Everyone assignable, with "Myself" first so a personal to-do stays one keystroke away.
  const assignable = useMemo(() => [
    ...(user?.id ? [{ id: String(user.id), name: `Myself (${user.name || 'Me'})` }] : []),
    ...users.filter(u => String(u.id) !== String(user?.id)).map(u => ({ id: String(u.id), name: u.name })),
  ], [users, user]);
  const assigneeName = useCallback(
    (id: string) => assignable.find(u => u.id === id)?.name || id,
    [assignable],
  );
  // Already-picked people are chips in the field, so drop them from the list.
  const asgMatches = useMemo(() => {
    const q = asgQuery.trim().toLowerCase();
    return assignable
      .filter(u => !nf.assigned_to.includes(u.id))
      .filter(u => !q || u.name.toLowerCase().includes(q));
  }, [assignable, nf.assigned_to, asgQuery]);
  const [creating, setCreating] = useState(false);

  const endRef = useRef<HTMLDivElement>(null);

  // ── Badge polling (always on while logged in) ──
  const loadUnread = useCallback(async () => {
    try { const r = await taskChatApi.unreadCount(); setUnread(r.data?.unread || 0); } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    if (!user) return;
    loadUnread();
    const t = setInterval(() => { if (!document.hidden) loadUnread(); }, 20000);
    return () => clearInterval(t);
  }, [user, loadUnread]);

  // ── Presence: heartbeat + fetch the online map (every 25s while logged in) ──
  const pulse = useCallback(async () => {
    try { await taskChatApi.heartbeat(); } catch { /* ignore */ }
    try { const r = await taskChatApi.presence(); setPresence(r.data || {}); } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    if (!user) return;
    pulse();
    const t = setInterval(() => { if (!document.hidden) pulse(); }, 25000);
    return () => clearInterval(t);
  }, [user, pulse]);
  const isOnline = (id?: string) => !!(id && presence[id]?.online);
  const lastSeenText = (id?: string) => {
    if (!id) return '';
    if (presence[id]?.online) return 'online';
    const ls = presence[id]?.last_seen;
    return ls ? `last seen ${fmtTime(ls)}` : 'offline';
  };

  const loadPeople = useCallback(async () => {
    setLoading(true);
    try { const r = await taskChatApi.people(); setPeople(r.data || []); }
    catch (e: any) { showError('Error', e?.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, [showError]);

  const loadTasks = useCallback(async (userId: string) => {
    setLoading(true);
    try { const r = await taskChatApi.tasks({ with_user: userId }); setTasks(r.data || []); }
    catch (e: any) { showError('Error', e?.message || 'Failed to load tasks'); }
    finally { setLoading(false); }
  }, [showError]);

  const loadMessages = useCallback(async (taskId: number) => {
    try { const r = await taskChatApi.messages(taskId); setMessages(r.data || []); }
    catch (e: any) { showError('Error', e?.message || 'Failed to load messages'); }
  }, [showError]);

  // Open a task thread directly (used by list clicks + deep-link).
  const openTask = useCallback(async (task: ChatTask) => {
    setActiveTask(task);
    setActiveUser({ id: task.counterpart_id, name: task.counterpart_name });
    setEditing(false);
    setSwipedId(null);
    setView('thread');
    setOpen(true);
    await loadMessages(task.id);
    loadUnread();
  }, [loadMessages, loadUnread]);

  // Deep-link: /?taskchat=<id> opens that task's thread. Runs on mount AND on
  // any client-side navigation (e.g. clicking a task notification in the bell),
  // then strips the param so it doesn't retrigger.
  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(location.search);
    const id = Number(params.get('taskchat'));
    if (!id) return;
    (async () => {
      try { const r = await taskChatApi.detail(id); if (r.data) await openTask(r.data); } catch { /* ignore */ }
      params.delete('taskchat');
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, location.search]);

  // Load people whenever the panel is opened to the people view.
  useEffect(() => { if (open && view === 'people') loadPeople(); }, [open, view, loadPeople]);

  // Poll the open thread every 5s (marks read as a side-effect).
  useEffect(() => {
    if (view !== 'thread' || !activeTask) return;
    const t = setInterval(() => { if (!document.hidden) loadMessages(activeTask.id); }, 5000);
    return () => clearInterval(t);
  }, [view, activeTask, loadMessages]);

  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [messages]);

  // Client-side task sorting. Open tasks always float above closed ones; then
  // by the chosen key (recent activity / priority high→low / earliest due).
  const sortList = useCallback((list: ChatTask[]) => {
    const openRank = (s: string) => (isOpenStatus(s) ? 0 : 1);
    const prRank: Record<string, number> = { high: 0, normal: 1, low: 2 };
    return [...list].sort((a, b) => {
      const o = openRank(a.status) - openRank(b.status);
      if (o) return o;
      if (taskSort === 'priority') {
        const p = (prRank[a.priority] ?? 1) - (prRank[b.priority] ?? 1);
        if (p) return p;
        return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
      }
      if (taskSort === 'due') {
        const ad = a.due_date ? new Date(a.due_date).getTime() : Infinity;
        const bd = b.due_date ? new Date(b.due_date).getTime() : Infinity;
        if (ad !== bd) return ad - bd; // earliest / most overdue first, nulls last
        return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
      }
      return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(); // recent
    });
  }, [taskSort]);
  const displayTasks = useMemo(() => sortList(tasks.filter(t =>
    taskFilter === 'all' ? true : taskFilter === 'pending' ? isOpenStatus(t.status) : !isOpenStatus(t.status))),
    [tasks, taskFilter, sortList]);
  const boardSorted = useMemo(() => sortList(boardTasks), [boardTasks, sortList]);

  // Global board loader (server-side direction / status / search / overdue).
  const loadBoard = useCallback(async () => {
    setLoading(true);
    try {
      const r = await taskChatApi.tasks({
        direction: boardDir === 'all' ? undefined : boardDir,
        status: taskFilter === 'all' ? undefined : taskFilter,
        search: boardDebounced || undefined,
        overdue: boardOverdue || undefined,
      });
      setBoardTasks(r.data || []);
    } catch (e: any) { showError('Error', e?.message || 'Failed to load tasks'); }
    finally { setLoading(false); }
  }, [boardDir, taskFilter, boardDebounced, boardOverdue, showError]);
  useEffect(() => { const t = setTimeout(() => setBoardDebounced(boardSearch), 300); return () => clearTimeout(t); }, [boardSearch]);
  useEffect(() => { if (open && view === 'board') loadBoard(); }, [open, view, loadBoard]);

  const openPeople = () => { setView('people'); setActiveUser(null); setActiveTask(null); setSwipedId(null); loadPeople(); loadUnread(); };
  const pickUser = (p: ChatPerson) => { setActiveUser({ id: p.user_id, name: p.user_name }); setSwipedId(null); setView('tasks'); loadTasks(p.user_id); };

  // Reload whichever task list is currently on screen (per-person or board).
  const refreshList = () => { if (view === 'board') loadBoard(); else if (activeUser) loadTasks(activeUser.id); };
  // Change a specific task's status straight from the list (no thread open).
  const setStatusFor = async (id: number, status: string) => {
    try {
      await taskChatApi.update(id, { status });
      showSuccess('Updated', statusLabel[status]);
      setSwipedId(null); refreshList(); loadUnread();
    } catch (e: any) { showError('Error', e?.message || 'Failed to update'); }
  };
  // Contextual quick actions revealed under a swiped row (role + stage aware).
  const quickActions = (t: ChatTask): { label: string; cls: string; fn: () => void }[] => {
    if (t.status === 'pending' || t.status === 'in_progress')
      return [{ label: t.i_am_creator ? 'Done' : 'Complete', cls: 'bg-emerald-600', fn: () => setStatusFor(t.id, t.i_am_creator ? 'done' : 'awaiting_approval') }];
    if (t.status === 'awaiting_approval')
      return t.i_am_creator
        ? [{ label: 'Approve', cls: 'bg-emerald-600', fn: () => setStatusFor(t.id, 'done') }, { label: 'Back', cls: 'bg-gray-500', fn: () => setStatusFor(t.id, 'in_progress') }]
        : [{ label: 'Undo', cls: 'bg-gray-500', fn: () => setStatusFor(t.id, 'in_progress') }];
    if (t.i_am_creator) return [{ label: 'Reopen', cls: 'bg-blue-600', fn: () => setStatusFor(t.id, 'in_progress') }]; // done / cancelled
    return [];
  };

  // Shared task-row renderer — swipe left (or tap the ‹ handle) to reveal quick
  // actions; tap the row to open the conversation.
  const taskRow = (t: ChatTask) => {
    const od = overdueDays(t);
    const actions = quickActions(t);
    const isOpen = swipedId === t.id;
    const width = actions.length * 74;
    return (
      <div key={t.id} className="relative overflow-hidden border-b border-gray-100">
        {actions.length > 0 && (
          <div className="absolute inset-y-0 right-0 flex">
            {actions.map((a, i) => (
              <button key={i} onClick={a.fn} style={{ width: 74 }}
                className={`${a.cls} text-white text-[11px] font-semibold flex items-center justify-center`}>{a.label}</button>
            ))}
          </div>
        )}
        <div
          className="relative bg-white transition-transform duration-200 ease-out"
          style={{ transform: isOpen ? `translateX(-${width}px)` : 'translateX(0)' }}
          onTouchStart={e => { swipeStartX.current = e.touches[0].clientX; }}
          onTouchEnd={e => {
            if (swipeStartX.current == null) return;
            const dx = e.changedTouches[0].clientX - swipeStartX.current;
            if (dx < -40 && actions.length) setSwipedId(t.id);
            else if (dx > 40) setSwipedId(null);
            swipeStartX.current = null;
          }}
        >
          <div role="button" tabIndex={0} onClick={() => (isOpen ? setSwipedId(null) : openTask(t))}
            className="w-full px-3 py-2.5 hover:bg-gray-50 text-left cursor-pointer">
            <div className="flex items-center gap-2">
              <span className={`h-2 w-2 rounded-full shrink-0 ${statusDot[t.status]}`} title={statusLabel[t.status]} />
              <span className="font-semibold text-gray-800 text-sm truncate flex-1 min-w-0">{t.title}</span>
              {t.due_date && (
                <span className="text-[11px] text-gray-500 flex items-center gap-0.5 shrink-0"><Clock size={11} />{fmtDate(t.due_date)}</span>
              )}
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold shrink-0 capitalize ${priorityStyle[t.priority]}`}>{t.priority}</span>
              {od > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold shrink-0 bg-red-500 text-white">{od}d overdue</span>}
              {t.unread > 0 && <span className="h-5 min-w-[20px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center shrink-0">{t.unread}</span>}
            </div>
            <div className="text-[12px] text-gray-500 truncate mt-1 pl-4 pr-6">
              {t.i_am_creator ? `→ ${t.counterpart_name}` : `from ${t.counterpart_name}`} · {t.description || t.last_msg || '—'}
            </div>
          </div>
          {actions.length > 0 && (
            <button onClick={() => setSwipedId(isOpen ? null : t.id)}
              title={isOpen ? 'Close' : 'Quick actions'}
              className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-600 p-1">
              <ChevronLeft size={15} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>
          )}
        </div>
      </div>
    );
  };

  const send = async () => {
    if (!activeTask || !draft.trim() || sending) return;
    setSending(true);
    const text = draft.trim();
    setDraft('');
    try {
      await taskChatApi.send(activeTask.id, text);
      await loadMessages(activeTask.id);
    } catch (e: any) { showError('Error', e?.message || 'Failed to send'); setDraft(text); }
    finally { setSending(false); }
  };

  // ── Edit task (creator only) ──
  const [editing, setEditing] = useState(false);
  const [ef, setEf] = useState({ title: '', priority: 'normal', due_date: '', description: '' });
  const openEdit = () => {
    if (!activeTask) return;
    setEf({
      title: activeTask.title, priority: activeTask.priority,
      due_date: activeTask.due_date ? String(activeTask.due_date).slice(0, 10) : '',
      description: activeTask.description || '',
    });
    setEditing(true);
  };
  const saveEdit = async () => {
    if (!activeTask || !ef.title.trim()) { showError('Validation', 'Title is required'); return; }
    try {
      await taskChatApi.update(activeTask.id, { title: ef.title.trim(), priority: ef.priority, due_date: ef.due_date || '', description: ef.description });
      setActiveTask({ ...activeTask, title: ef.title.trim(), priority: ef.priority as ChatTask['priority'], due_date: ef.due_date || null, description: ef.description || null });
      if (activeUser) loadTasks(activeUser.id);
      setEditing(false);
      showSuccess('Saved', 'Task updated');
    } catch (e: any) { showError('Error', e?.message || 'Failed to save'); }
  };

  const openNew = async () => {
    setView('new');
    // Pre-tick whoever's thread we came from, unless that's a group entry.
    const seed = activeUser?.id && !String(activeUser.id).startsWith('group:') ? [String(activeUser.id)] : [];
    setNf({ title: '', assigned_to: seed, priority: 'normal', due_date: '', message: '' });
    // Clear the filter box too, or reopening the form shows a stale query.
    setAsgQuery('');
    setAsgOpen(false);
    if (users.length === 0) {
      try { const r = await taskChatApi.users(); setUsers(r.data || []); } catch { /* ignore */ }
    }
  };
  const createTask = async () => {
    if (!nf.title.trim() || nf.assigned_to.length === 0) { showError('Validation', 'Title and at least one assignee are required'); return; }
    setCreating(true);
    try {
      await taskChatApi.create({ title: nf.title.trim(), assigned_to: nf.assigned_to, priority: nf.priority, due_date: nf.due_date || undefined, message: nf.message || undefined });
      showSuccess('Created', nf.assigned_to.length > 1 ? `Task assigned to ${nf.assigned_to.length} people` : 'Task assigned');
      openPeople();
    } catch (e: any) { showError('Error', e?.message || 'Failed to create task'); }
    finally { setCreating(false); }
  };

  // ── App-icon / tab badge + desktop popup on new messages ──
  // Web Push (OS notifications) is disabled app-wide by the self-destroying
  // service worker, so we drive alerts client-side while the app is open:
  //  • setAppBadge → count on the installed-PWA app icon
  //  • document.title → "(N) …" count in the browser tab
  //  • Notification API (no SW needed) → a popup when the unread count rises
  useEffect(() => {
    try {
      const nav = navigator as any;
      if ('setAppBadge' in nav) { if (unread > 0) nav.setAppBadge(unread).catch?.(() => {}); else nav.clearAppBadge?.(); }
    } catch { /* ignore */ }
    const clean = document.title.replace(/^\(\d+\)\s*/, '');
    document.title = unread > 0 ? `(${unread}) ${clean}` : clean;
    baseTitleRef.current = clean;

    // Only popup on a genuine increase after the first load (not for unread
    // that already existed when the app opened).
    if (!isNativeApp && !firstUnreadRef.current && unread > prevUnreadRef.current && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      taskChatApi.people().then(r => {
        const p = (r.data || []).find(x => x.unread > 0);
        const note = new Notification(p ? `New message from ${p.user_name}` : 'New task message',
          { body: p?.last_msg || `${unread} unread message(s)`, tag: 'task-chat' });
        note.onclick = () => { window.focus(); setOpen(true); if (p) pickUser(p); else setView('people'); };
      }).catch(() => { /* ignore */ });
    }
    firstUnreadRef.current = false;
    prevUnreadRef.current = unread;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unread]);
  // Ask for notification permission (browser only — the Android app shows
  // background notifications via its native NotificationPollService instead).
  const requestNotifPerm = () => {
    try { if (!isNativeApp && typeof Notification !== 'undefined' && Notification.permission === 'default') Notification.requestPermission().catch(() => {}); } catch { /* ignore */ }
  };

  // ── Draggable launcher ──
  const LSIZE = 56; // h-14 w-14
  const clampPos = (x: number, y: number) => ({
    x: Math.min(Math.max(4, x), window.innerWidth - LSIZE - 4),
    y: Math.min(Math.max(4, y), window.innerHeight - LSIZE - 4),
  });
  // Persist + keep on-screen after resize.
  useEffect(() => {
    if (!launcherPos) return;
    try { localStorage.setItem('taskchat_launcher_pos', JSON.stringify(launcherPos)); } catch { /* ignore */ }
  }, [launcherPos]);
  useEffect(() => {
    const onResize = () => setLauncherPos(p => (p ? clampPos(p.x, p.y) : p));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const onLauncherDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    dragRef.current = { dragging: true, moved: false, startX: e.clientX, startY: e.clientY, offX: e.clientX - rect.left, offY: e.clientY - rect.top };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onLauncherMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const d = dragRef.current;
    if (!d.dragging) return;
    if (!d.moved && Math.abs(e.clientX - d.startX) + Math.abs(e.clientY - d.startY) > 6) d.moved = true;
    if (!d.moved) return;
    setLauncherPos(clampPos(e.clientX - d.offX, e.clientY - d.offY));
  };
  const onLauncherUp = () => { dragRef.current.dragging = false; };
  const onLauncherClick = () => {
    if (dragRef.current.moved) { dragRef.current.moved = false; return; } // was a drag, not a tap
    requestNotifPerm();
    setOpen(true); setView('people');
  };

  if (!user) return null;

  return (
    <>
      {/* Floating launcher — draggable; tap to open. */}
      {!open && (
        <button
          onClick={onLauncherClick}
          onPointerDown={onLauncherDown}
          onPointerMove={onLauncherMove}
          onPointerUp={onLauncherUp}
          style={launcherPos ? { left: launcherPos.x, top: launcherPos.y } : undefined}
          className={`fixed z-[70] h-14 w-14 rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg flex items-center justify-center touch-none cursor-grab active:cursor-grabbing ${launcherPos ? '' : 'bottom-5 right-5'}`}
          title="Tasks & chat (drag to move)">
          <MessageSquare size={24} />
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center">{unread > 99 ? '99+' : unread}</span>
          )}
        </button>
      )}

      {/* Panel */}
      {open && (
        <div className="fixed bottom-5 right-5 z-[70] w-[92vw] sm:w-96 h-[560px] max-h-[85vh] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center gap-2 px-3 py-2.5 bg-blue-600 text-white">
            {(view === 'tasks' || view === 'thread' || view === 'new') && (
              <button onClick={() => (view === 'thread' ? (activeUser ? (setView('tasks'), loadTasks(activeUser.id)) : openPeople()) : openPeople())}
                className="p-1 hover:bg-white/15 rounded"><ChevronLeft size={18} /></button>
            )}
            <MessageSquare size={18} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold leading-tight truncate">
                {view === 'people' || view === 'board' ? 'Tasks & Chat'
                  : view === 'new' ? 'New Task'
                  : view === 'tasks' ? activeUser?.name
                  : activeTask?.title}
              </div>
              {view === 'thread' && activeUser && (
                <div className="text-[11px] text-blue-100 truncate flex items-center gap-1">
                  <span className={`h-2 w-2 rounded-full ${isOnline(activeUser.id) ? 'bg-emerald-400' : 'bg-gray-300'}`} />
                  {activeUser.name} · {lastSeenText(activeUser.id)}
                </div>
              )}
            </div>
            {(view === 'people' || view === 'board') && (
              <button onClick={openNew} className="p-1 hover:bg-white/15 rounded" title="New task"><Plus size={18} /></button>
            )}
            {view === 'thread' && activeTask?.i_am_creator && !editing && (
              <button onClick={openEdit} className="p-1 hover:bg-white/15 rounded" title="Edit task"><Pencil size={16} /></button>
            )}
            <button onClick={() => setOpen(false)} className="p-1 hover:bg-white/15 rounded"><X size={18} /></button>
          </div>

          {/* People / Tasks top toggle */}
          {(view === 'people' || view === 'board') && (
            <div className="flex gap-1 px-3 py-2 bg-white border-b border-gray-100 flex-none">
              <button onClick={() => setView('people')}
                className={`flex-1 py-1.5 rounded-md text-[12px] font-semibold ${view === 'people' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>People</button>
              <button onClick={() => setView('board')}
                className={`flex-1 py-1.5 rounded-md text-[12px] font-semibold ${view === 'board' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>All Tasks</button>
            </div>
          )}

          {/* Body */}
          <div className="flex-1 min-h-0 overflow-y-auto bg-gray-50">
            {loading && <div className="flex justify-center py-8 text-gray-400"><Loader2 className="animate-spin" size={20} /></div>}

            {/* People — only those with at least one pending task */}
            {!loading && view === 'people' && (() => {
              const meId = String(user?.id ?? '');
              const isSelf = (p: ChatPerson) => String(p.user_id) === meId;
              // Pin the "Myself" row (self-assigned to-dos) to the top so it's
              // always visible after creating a task to yourself.
              const visiblePeople = people
                .filter(p => (p.pending_count || 0) > 0)
                .sort((a, b) => (isSelf(a) ? -1 : 0) - (isSelf(b) ? -1 : 0));
              return visiblePeople.length === 0 ? (
                <div className="text-center text-sm text-gray-400 py-10 px-4">No pending tasks.<br />Tap <Plus size={12} className="inline" /> to assign one.</div>
              ) : visiblePeople.map(p => (
                <button key={p.user_id} onClick={() => pickUser(p)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 hover:bg-white border-b border-gray-100 text-left ${isSelf(p) ? 'bg-blue-50/60' : ''}`}>
                  <div className="relative shrink-0">
                    <div className={`h-10 w-10 rounded-full font-bold text-sm flex items-center justify-center ${p.is_group ? 'bg-violet-100 text-violet-700' : 'bg-blue-100 text-blue-700'}`}>
                      {p.is_group ? <Users size={18} /> : (isSelf(p) ? '★' : initials(p.user_name))}
                    </div>
                    {/* A group has no single person, so no presence dot. */}
                    {!p.is_group && (
                      <span className={`absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white ${isOnline(p.user_id) ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-gray-800 text-sm truncate">{p.is_group ? p.user_name : (isSelf(p) ? 'My Tasks (Myself)' : p.user_name)}</span>
                      <span className="text-[10px] text-gray-400 shrink-0">{fmtTime(p.last_at)}</span>
                    </div>
                    <div className="text-[12px] text-gray-500 truncate">
                      {p.is_group && <span className="text-violet-600 font-medium">Group · {p.member_count} people · </span>}
                      {p.last_msg || `${p.pending_count} pending task(s)`}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    {p.pending_count > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">{p.pending_count}</span>}
                    {p.unread > 0 && <span className="h-5 min-w-[20px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{p.unread}</span>}
                  </div>
                </button>
              ));
            })()}

            {/* Tasks for a person */}
            {!loading && view === 'tasks' && tasks.length > 0 && (
              <div className="sticky top-0 z-10 bg-white border-b border-gray-100">
                {/* Status filter tabs */}
                <div className="flex items-center gap-1 px-3 pt-2">
                  {([['pending', 'Pending'], ['completed', 'Completed'], ['all', 'All']] as const).map(([k, label]) => (
                    <button key={k} onClick={() => setTaskFilter(k)}
                      className={`flex-1 py-1 rounded-md text-[12px] font-semibold transition-colors ${taskFilter === k ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{label}</button>
                  ))}
                </div>
                {/* Sort */}
                <div className="flex items-center gap-1 px-3 py-1.5 text-[11px]">
                  <span className="text-gray-400">Sort:</span>
                  {([['recent', 'Recent'], ['priority', 'Priority'], ['due', 'Date']] as const).map(([k, label]) => (
                    <button key={k} onClick={() => setTaskSort(k)}
                      className={`px-2 py-0.5 rounded-full font-medium ${taskSort === k ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100'}`}>{label}</button>
                  ))}
                </div>
              </div>
            )}
            {!loading && view === 'tasks' && (
              displayTasks.length === 0 ? (
                <div className="text-center text-sm text-gray-400 py-10">
                  {tasks.length === 0 ? `No tasks with ${activeUser?.name}.` : `No ${taskFilter === 'all' ? '' : taskFilter} tasks.`}
                </div>
              ) : displayTasks.map(taskRow)
            )}

            {/* Global task board */}
            {view === 'board' && (
              <div className="sticky top-0 z-10 bg-white border-b border-gray-100 p-2 space-y-2">
                <div className="relative">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input value={boardSearch} onChange={e => setBoardSearch(e.target.value)} placeholder="Search tasks…"
                    className="w-full pl-8 pr-7 py-1.5 border border-gray-300 rounded text-[13px] focus:ring-1 focus:ring-blue-400 outline-none" />
                  {boardSearch && <button onClick={() => setBoardSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><X size={13} /></button>}
                </div>
                <div className="flex items-center gap-1 text-[11px]">
                  {([['all', 'All'], ['to_me', 'To me'], ['by_me', 'By me']] as const).map(([k, label]) => (
                    <button key={k} onClick={() => setBoardDir(k)}
                      className={`px-2 py-0.5 rounded-full font-medium ${boardDir === k ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{label}</button>
                  ))}
                  <button onClick={() => setBoardOverdue(v => !v)}
                    className={`ml-auto px-2 py-0.5 rounded-full font-medium ${boardOverdue ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>Overdue</button>
                </div>
                <div className="flex items-center gap-1 text-[11px]">
                  {([['pending', 'Pending'], ['completed', 'Completed'], ['all', 'All']] as const).map(([k, label]) => (
                    <button key={k} onClick={() => setTaskFilter(k)}
                      className={`px-2 py-0.5 rounded-full font-medium ${taskFilter === k ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100'}`}>{label}</button>
                  ))}
                  <span className="ml-auto text-gray-400">Sort:</span>
                  {([['recent', 'Recent'], ['priority', 'Priority'], ['due', 'Date']] as const).map(([k, label]) => (
                    <button key={k} onClick={() => setTaskSort(k)}
                      className={`px-1.5 py-0.5 rounded-full font-medium ${taskSort === k ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100'}`}>{label}</button>
                  ))}
                </div>
              </div>
            )}
            {!loading && view === 'board' && (
              boardSorted.length === 0 ? (
                <div className="text-center text-sm text-gray-400 py-10">No tasks match.</div>
              ) : boardSorted.map(taskRow)
            )}

            {/* Thread */}
            {view === 'thread' && activeTask && (
              <div className="p-3 space-y-2">
                {editing && (
                  <div className="bg-white rounded-lg border border-blue-200 p-2.5 space-y-2">
                    <input value={ef.title} onChange={e => setEf(s => ({ ...s, title: e.target.value }))}
                      placeholder="Task title" className="w-full border border-gray-300 rounded px-2.5 py-1.5 text-sm font-semibold focus:ring-1 focus:ring-blue-400 outline-none" />
                    <div className="flex gap-2">
                      <select value={ef.priority} onChange={e => setEf(s => ({ ...s, priority: e.target.value }))}
                        className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-sm bg-white focus:ring-1 focus:ring-blue-400 outline-none">
                        <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option>
                      </select>
                      <input type="date" value={ef.due_date} onChange={e => setEf(s => ({ ...s, due_date: e.target.value }))}
                        className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-1 focus:ring-blue-400 outline-none" />
                    </div>
                    <textarea value={ef.description} onChange={e => setEf(s => ({ ...s, description: e.target.value }))} rows={2}
                      placeholder="Remark / description" className="w-full border border-gray-300 rounded px-2.5 py-1.5 text-sm focus:ring-1 focus:ring-blue-400 outline-none resize-none" />
                    <div className="flex gap-2">
                      <button onClick={saveEdit} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-[12px] font-semibold rounded py-1.5">Save</button>
                      <button onClick={() => setEditing(false)} className="flex-1 border border-gray-300 text-gray-600 text-[12px] font-semibold rounded py-1.5 hover:bg-gray-50">Cancel</button>
                    </div>
                  </div>
                )}
                {activeTask.description && <div className="text-[12px] text-gray-600 bg-white rounded-lg border border-gray-100 px-2.5 py-2">{activeTask.description}</div>}
                {messages.length === 0 && <div className="text-center text-[12px] text-gray-400 py-6">No messages yet. Say something 👋</div>}
                {messages.map(m => (
                  <div key={m.id} className={`flex ${m.mine ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[78%] rounded-2xl px-3 py-1.5 text-sm ${m.mine ? 'bg-blue-600 text-white rounded-br-sm' : 'bg-white text-gray-800 border border-gray-200 rounded-bl-sm'}`}>
                      {/* In a group there are several possible senders, so name
                          the author. Pointless on a 1-to-1 thread. */}
                      {activeTask.is_group && !m.mine && (
                        <div className="text-[11px] font-semibold text-violet-700 mb-0.5">{m.sender_name}</div>
                      )}
                      <div className="whitespace-pre-wrap break-words">{m.body}</div>
                      <div className={`text-[10px] mt-0.5 flex items-center gap-1 justify-end ${m.mine ? 'text-blue-100' : 'text-gray-400'}`}>
                        {fmtTime(m.created_at)}
                        {/* Group receipt: ✓✓ only once EVERYONE else has read it;
                            until then show how many of them have. */}
                        {m.mine && activeTask.is_group && !m.seen && (m.read_count ?? 0) > 0 && (
                          <span className="text-blue-100">{m.read_count}/{m.audience}</span>
                        )}
                        {m.mine && (m.seen ? <CheckCheck size={13} className="text-sky-200" /> : <Check size={13} className="text-blue-200" />)}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={endRef} />
              </div>
            )}

            {/* New task */}
            {view === 'new' && (
              <div className="p-3 space-y-2.5">
                <div>
                  <label className="text-[11px] font-semibold text-gray-500 uppercase">Title</label>
                  <input value={nf.title} onChange={e => setNf(s => ({ ...s, title: e.target.value }))}
                    placeholder="What needs doing?" className="mt-1 w-full border border-gray-300 rounded px-2.5 py-1.5 text-sm focus:ring-1 focus:ring-blue-400 outline-none" />
                </div>
                <div>
                  <label className="text-[11px] font-semibold text-gray-500 uppercase">
                    Assign to
                    {nf.assigned_to.length > 1 && (
                      <span className="ml-1 normal-case font-medium text-blue-600">
                        · group of {nf.assigned_to.length} (one shared chat)
                      </span>
                    )}
                  </label>
                  {/* Type-to-filter multi-select. Chosen people become chips in
                      the field; picking more than one makes it a group task —
                      one task, one thread, everyone in it. */}
                  <div className="relative mt-1">
                    <div
                      onClick={() => { setAsgOpen(true); asgInputRef.current?.focus(); }}
                      className="border border-gray-300 rounded bg-white px-1.5 py-1 flex flex-wrap items-center gap-1 cursor-text focus-within:ring-1 focus-within:ring-blue-400">
                      {nf.assigned_to.map(id => (
                        <span key={id} className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-[12px] font-medium rounded px-1.5 py-0.5">
                          {assigneeName(id)}
                          <button type="button"
                            onClick={e => { e.stopPropagation(); toggleAssignee(id); }}
                            className="hover:text-blue-950" aria-label={`Remove ${assigneeName(id)}`}>
                            <X size={11} />
                          </button>
                        </span>
                      ))}
                      <input
                        ref={asgInputRef}
                        value={asgQuery}
                        onChange={e => { setAsgQuery(e.target.value); setAsgOpen(true); }}
                        onFocus={() => setAsgOpen(true)}
                        // Blur is deferred so a click on a result registers first.
                        onBlur={() => setTimeout(() => setAsgOpen(false), 150)}
                        onKeyDown={e => {
                          // Enter takes the top match; Backspace on an empty box
                          // peels off the last chip — standard for a chip field.
                          if (e.key === 'Enter' && asgMatches[0]) { e.preventDefault(); toggleAssignee(asgMatches[0].id); setAsgQuery(''); }
                          else if (e.key === 'Backspace' && !asgQuery && nf.assigned_to.length) { toggleAssignee(nf.assigned_to[nf.assigned_to.length - 1]); }
                          else if (e.key === 'Escape') { setAsgOpen(false); }
                        }}
                        placeholder={nf.assigned_to.length ? 'Add another…' : 'Type a name…'}
                        className="flex-1 min-w-[90px] outline-none text-sm px-1 py-0.5 bg-transparent" />
                    </div>

                    {asgOpen && (
                      <div className="absolute z-30 left-0 right-0 mt-1 border border-gray-300 rounded bg-white shadow-lg max-h-40 overflow-y-auto divide-y divide-gray-100">
                        {asgMatches.length === 0 ? (
                          <div className="px-2.5 py-2 text-[12px] text-gray-400">
                            {asgQuery ? `No one matching “${asgQuery}”` : 'Everyone is already added'}
                          </div>
                        ) : asgMatches.map(u => (
                          <button key={u.id} type="button"
                            onMouseDown={e => e.preventDefault()}
                            onClick={() => { toggleAssignee(u.id); setAsgQuery(''); asgInputRef.current?.focus(); }}
                            className="w-full text-left px-2.5 py-1.5 text-sm hover:bg-blue-50">
                            {u.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="text-[11px] font-semibold text-gray-500 uppercase">Priority</label>
                    <select value={nf.priority} onChange={e => setNf(s => ({ ...s, priority: e.target.value }))}
                      className="mt-1 w-full border border-gray-300 rounded px-2.5 py-1.5 text-sm bg-white focus:ring-1 focus:ring-blue-400 outline-none">
                      <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option>
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="text-[11px] font-semibold text-gray-500 uppercase">Due</label>
                    <input type="date" value={nf.due_date} onChange={e => setNf(s => ({ ...s, due_date: e.target.value }))}
                      className="mt-1 w-full border border-gray-300 rounded px-2.5 py-1.5 text-sm focus:ring-1 focus:ring-blue-400 outline-none" />
                  </div>
                </div>
                <div>
                  <label className="text-[11px] font-semibold text-gray-500 uppercase">First message (optional)</label>
                  <textarea value={nf.message} onChange={e => setNf(s => ({ ...s, message: e.target.value }))} rows={2}
                    placeholder="Add details…" className="mt-1 w-full border border-gray-300 rounded px-2.5 py-1.5 text-sm focus:ring-1 focus:ring-blue-400 outline-none resize-none" />
                </div>
                <button onClick={createTask} disabled={creating}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white text-sm font-semibold rounded py-2 flex items-center justify-center gap-2">
                  {creating ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle2 size={16} />} Create &amp; Assign
                </button>
              </div>
            )}
          </div>

          {/* Footer — thread composer */}
          {view === 'thread' && activeTask && (
            <div className="flex items-center gap-2 p-2 border-t border-gray-200 bg-white">
              <input value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), send())}
                placeholder="Type a message…" className="flex-1 border border-gray-300 rounded-full px-3.5 py-2 text-sm focus:ring-1 focus:ring-blue-400 outline-none" />
              <button onClick={send} disabled={!draft.trim() || sending}
                className="h-9 w-9 rounded-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white flex items-center justify-center shrink-0">
                {sending ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
